// TAVEN Learning Center — expansion courses (batch 1, fully authored).
// Same rich shape as flagship courses: modules, lessons + voice-over, per-module
// quizzes, a final assessment, objectives, resources, instructor, and EN/FR/HT/ES
// metadata. Rendered by RichCoursePage via CourseRoute.

const q = (question, options, answer) => ({ q: question, options, answer });

export const EXPANSION_COURSES = [
  {
    slug: "dropshipping",
    title: "Dropshipping Mastery",
    category: "Commerce & Marketplace",
    level: "Beginner",
    duration: "2h 40m",
    languages: ["English", "Français", "Kreyòl", "Español"],
    rich: true,
    instructor: "amara",
    summary: "Start a lean online store without holding inventory — source smart, price for profit, and keep customers happy.",
    description: "Dropshipping lets you sell products without buying stock upfront. In this course you'll learn how the model really works, how to pick reliable suppliers and winning products, how to price so you actually profit after fees and shipping, and how to handle orders and customer expectations so your store earns trust.",
    objectives: [
      "Explain how the dropshipping model works end to end",
      "Choose reliable suppliers and evaluate product demand",
      "Price products to stay competitive while protecting margin",
      "Set clear shipping expectations and handle issues professionally",
    ],
    i18n: {
      fr: { title: "Maîtrise du Dropshipping", summary: "Lancez une boutique en ligne légère sans stock — sourcez intelligemment, fixez des prix rentables et satisfaites vos clients.",
        objectives: ["Expliquer le fonctionnement du dropshipping de bout en bout", "Choisir des fournisseurs fiables et évaluer la demande", "Fixer des prix compétitifs tout en protégeant la marge", "Définir des délais de livraison clairs et gérer les problèmes"] },
      ht: { title: "Metriz Dropshipping", summary: "Louvri yon boutik anliy san estòk — jwenn bon founisè, fikse pri ki bay pwofi, epi kenbe kliyan yo kontan.",
        objectives: ["Eksplike kijan dropshipping ap fonksyone", "Chwazi founisè serye epi evalye demann", "Fikse pri konpetitif san pèdi maj", "Bay dat livrezon klè epi jere pwoblèm"] },
      es: { title: "Dominio del Dropshipping", summary: "Inicia una tienda ligera sin inventario — abastece con inteligencia, fija precios rentables y mantén felices a tus clientes.",
        objectives: ["Explicar cómo funciona el dropshipping de principio a fin", "Elegir proveedores confiables y evaluar la demanda", "Fijar precios competitivos protegiendo el margen", "Definir tiempos de envío claros y resolver problemas"] },
    },
    resources: [
      { title: "Supplier vetting checklist", type: "PDF" },
      { title: "Profit & fees calculator", type: "Sheet" },
      { title: "Customer shipping-notice template", type: "Doc" },
    ],
    modules: [
      {
        title: "How Dropshipping Really Works",
        avatar: "amara",
        avatarLine: "Hi, I'm Amara. Let's clear up what dropshipping actually is — and what it isn't — before you spend a dollar.",
        video: { title: "Module 1 Overview", duration: "5:40" },
        lessons: [
          { title: "The dropshipping model",
            content: "In dropshipping, you list a product, and when a customer buys it, your supplier ships it directly to them. You never hold stock. Your job is marketing, pricing, and customer experience — not warehousing.",
            voiceover: "Think of yourself as the storefront and the matchmaker. You bring the customer and the product together; the supplier handles the box. Your profit is the gap between what the customer pays and what the supplier charges you — minus fees." },
          { title: "Pros, cons, and honest expectations",
            content: "Low startup cost and no inventory risk are the big wins. The trade-offs are thinner margins, less control over shipping speed, and more competition. Success comes from choosing well and serving customers better than rivals.",
            voiceover: "Dropshipping is not free money. It rewards patience and good service. If two stores sell the same item, the one that communicates clearly and picks better products wins." },
        ],
        quiz: [
          q("In dropshipping, who physically ships the product to the customer?", ["You, from your garage", "The supplier, directly", "The customer picks it up", "A TAVEN warehouse"], 1),
          q("What is the store owner's main job in dropshipping?", ["Manufacturing", "Warehousing stock", "Marketing, pricing & customer experience", "Delivering packages"], 2),
        ],
      },
      {
        title: "Finding Suppliers & Winning Products",
        avatar: "amara",
        avatarLine: "A great product with a shaky supplier will sink you. Let's find both — the product AND the partner.",
        video: { title: "Module 2 Overview", duration: "6:05" },
        lessons: [
          { title: "Vetting reliable suppliers",
            content: "Order a sample yourself. Check processing time, packaging quality, and communication speed. A supplier who answers fast and ships on time is worth more than one who is a few cents cheaper.",
            voiceover: "Never sell what you haven't held. A sample tells you the truth about quality and timing that a product photo never will." },
          { title: "Spotting products people actually want",
            content: "Look for products that solve a clear problem, are hard to find locally, and photograph well. Avoid fragile, oversized, or heavily branded items. Demand plus healthy margin beats a flashy item with no profit.",
            voiceover: "The best products are useful, giftable, and light to ship. If you can explain the benefit in one sentence, customers can too." },
        ],
        quiz: [
          q("What's the best first step before selling a new product?", ["Run ads immediately", "Order a sample yourself", "Raise the price", "Copy a competitor's page"], 1),
          q("Which product trait usually makes dropshipping harder?", ["Solves a clear problem", "Lightweight", "Fragile & oversized", "Photographs well"], 2),
        ],
      },
      {
        title: "Pricing for Real Profit",
        avatar: "amara",
        avatarLine: "This is where most beginners lose money. Let's make sure your prices actually leave you with profit.",
        video: { title: "Module 3 Overview", duration: "5:15" },
        lessons: [
          { title: "Landed cost & margin",
            content: "Your true cost is the product price plus shipping plus payment fees. Set your selling price so that after all of these you keep a margin you're comfortable with — often 20% or more.",
            voiceover: "Always start from landed cost, never from the product price alone. Fees and shipping quietly eat beginners' profits." },
          { title: "Competitive but healthy pricing",
            content: "Check what similar items sell for, then position near or slightly below while keeping your margin safe. TAVEN's Price Guard can flag when a price is too low to be profitable.",
            voiceover: "Cheapest isn't the goal — profitable and trusted is. A fair price with great service beats a race to the bottom." },
        ],
        quiz: [
          q("Landed cost includes the product price plus…", ["Only shipping", "Shipping and payment fees", "Nothing else", "Your salary"], 1),
          q("A safe beginner margin target is often at least…", ["2%", "20%", "0%", "You should sell at cost"], 1),
        ],
      },
      {
        title: "Orders, Shipping & Customer Trust",
        avatar: "amara",
        avatarLine: "The sale is just the start. How you handle the wait decides whether they buy again.",
        video: { title: "Module 4 Overview", duration: "4:50" },
        lessons: [
          { title: "Setting shipping expectations",
            content: "State delivery windows clearly on the product page and in a confirmation message. Customers accept longer waits when they're told upfront — surprises cause complaints and refunds.",
            voiceover: "Under-promise and over-deliver on timing. 'Arrives in 7–15 days' set upfront beats a silent two-week wait every time." },
          { title: "Handling problems professionally",
            content: "When something goes wrong, respond quickly, take responsibility, and offer a clear fix — replacement, refund, or update. One well-handled issue can turn a frustrated buyer into a loyal one.",
            voiceover: "Problems are inevitable; poor responses are optional. Speed and honesty protect your reputation." },
        ],
        quiz: [
          q("Why state delivery windows upfront?", ["To scare customers", "Customers accept waits when told in advance", "It's required by law only", "To slow down orders"], 1),
          q("Best response to a shipping problem?", ["Ignore it", "Respond quickly with a clear fix", "Blame the customer", "Cancel silently"], 1),
        ],
      },
    ],
    finalAssessment: [
      q("Who ships the product in a dropshipping order?", ["The store owner", "The supplier directly", "TAVEN", "A courier hired by the buyer"], 1),
      q("What should you always do before selling a product?", ["Order a sample", "Double the price", "Delete reviews", "Hide shipping times"], 0),
      q("Landed cost is product price plus…", ["shipping and payment fees", "nothing", "only taxes", "your rent"], 0),
      q("Which pricing approach is healthiest?", ["Always cheapest", "Competitive while protecting margin", "As high as possible", "Random"], 1),
      q("The best way to keep customer trust during shipping delays is to…", ["say nothing", "set clear expectations upfront", "refuse refunds", "change the price"], 1),
    ],
  },

  {
    slug: "digital-marketing",
    title: "Digital Marketing Essentials",
    category: "Marketing & Growth",
    level: "Beginner",
    duration: "2h 55m",
    languages: ["English", "Français", "Kreyòl", "Español"],
    rich: true,
    instructor: "marcus",
    summary: "Attract the right buyers and turn attention into sales with content, email, and honest advertising.",
    description: "Marketing is how the right people discover what you sell. This course covers the core channels — content, social, email, and paid ads — plus the fundamentals of an offer and a funnel, so you can grow steadily without hype or wasted spend.",
    objectives: [
      "Understand the core digital marketing channels and when to use each",
      "Craft a clear offer and a simple sales funnel",
      "Write content and email that builds trust and drives action",
      "Run small, measured ad tests without wasting budget",
    ],
    i18n: {
      fr: { title: "Les Bases du Marketing Digital", summary: "Attirez les bons acheteurs et transformez l'attention en ventes grâce au contenu, à l'e-mail et à la publicité honnête.",
        objectives: ["Comprendre les principaux canaux et quand les utiliser", "Créer une offre claire et un tunnel de vente simple", "Rédiger du contenu et des e-mails qui inspirent confiance", "Lancer de petits tests publicitaires mesurés"] },
      ht: { title: "Baz Marketing Dijital", summary: "Atire bon achtè yo epi transfòme atansyon an vant ak kontni, imèl, ak piblisite onèt.",
        objectives: ["Konprann kanal prensipal yo epi kilè pou itilize yo", "Kreye yon òf klè ak yon tinèl vant senp", "Ekri kontni ak imèl ki bay konfyans", "Fè ti tès piblisite ak mezi"] },
      es: { title: "Fundamentos del Marketing Digital", summary: "Atrae a los compradores correctos y convierte la atención en ventas con contenido, correo y publicidad honesta.",
        objectives: ["Entender los canales principales y cuándo usarlos", "Crear una oferta clara y un embudo simple", "Escribir contenido y correos que generan confianza", "Ejecutar pruebas pequeñas y medidas de anuncios"] },
    },
    resources: [
      { title: "Offer & funnel worksheet", type: "PDF" },
      { title: "30-day content calendar", type: "Sheet" },
      { title: "Welcome email template", type: "Doc" },
    ],
    modules: [
      {
        title: "The Marketing Foundation",
        avatar: "marcus",
        avatarLine: "I'm Marcus. Before tactics, let's get the foundation right — a clear offer beats clever ads.",
        video: { title: "Module 1 Overview", duration: "6:00" },
        lessons: [
          { title: "Offer, audience, and message",
            content: "Great marketing starts with a clear offer (what you sell and why it helps), a specific audience (who it's for), and a simple message (one benefit they care about). Get these right and every channel works better.",
            voiceover: "If you can't say who it's for and why it helps in one line, no ad budget will save it. Clarity is the cheapest growth lever you have." },
          { title: "The simple funnel",
            content: "A funnel moves strangers to buyers in stages: awareness, interest, decision, action. Your job is to remove friction at each step and give the right nudge at the right time.",
            voiceover: "Think of it as a staircase, not a cliff. Each step should feel like the obvious next move." },
        ],
        quiz: [
          q("What should marketing start with?", ["A big ad budget", "A clear offer, audience, and message", "A logo", "A viral video"], 1),
          q("The stages of a simple funnel are awareness, interest, decision, and…", ["refund", "action", "return", "tax"], 1),
        ],
      },
      {
        title: "Content & Social",
        avatar: "marcus",
        avatarLine: "Content earns attention for free — if it's useful, honest, and consistent.",
        video: { title: "Module 2 Overview", duration: "5:30" },
        lessons: [
          { title: "Content that builds trust",
            content: "Teach, show, and tell stories rather than only selling. Helpful content earns trust so that when you do make an offer, people are ready to say yes.",
            voiceover: "Give before you ask. The creators who help most are the ones we buy from most." },
          { title: "Being consistent without burning out",
            content: "A steady, modest posting rhythm beats occasional bursts. Batch your content, repurpose your best posts, and focus on one or two channels you can sustain.",
            voiceover: "Consistency compounds. Two good posts a week for a year beats twenty in one month and silence after." },
        ],
        quiz: [
          q("What kind of content builds the most trust?", ["Only hard-sell ads", "Helpful, honest, story-driven content", "Random memes", "Nothing"], 1),
          q("Best approach to posting?", ["Occasional huge bursts", "Steady, sustainable rhythm", "Never post", "Copy others exactly"], 1),
        ],
      },
      {
        title: "Email That Converts",
        avatar: "marcus",
        avatarLine: "Email is the channel you own. Let's use it with respect and results.",
        video: { title: "Module 3 Overview", duration: "5:10" },
        lessons: [
          { title: "Building a permission list",
            content: "Invite people to subscribe with a clear benefit (a guide, a discount, updates). A small engaged list beats a big cold one. Always let people opt in and out freely.",
            voiceover: "Permission is everything. People who chose to hear from you are the warmest audience you'll ever have." },
          { title: "Writing emails people open",
            content: "Use a specific subject line, one clear idea, and one clear call to action. Write like a helpful human, not a billboard.",
            voiceover: "One email, one idea, one ask. Confusion is the enemy of the click." },
        ],
        quiz: [
          q("A healthy email list is built on…", ["buying addresses", "permission and clear benefit", "spamming", "guesswork"], 1),
          q("A good marketing email has…", ["five different asks", "one clear idea and call to action", "no subject", "only images"], 1),
        ],
      },
      {
        title: "Smart, Measured Advertising",
        avatar: "marcus",
        avatarLine: "Ads amplify what already works. Never pour money on a leaky funnel.",
        video: { title: "Module 4 Overview", duration: "5:20" },
        lessons: [
          { title: "Start small and test",
            content: "Begin with a small budget and one clear goal. Test one variable at a time (audience, image, or message) so you know what actually moved results.",
            voiceover: "Small tests, clear reads. Scaling a winner is easy; guessing is expensive." },
          { title: "Reading the numbers honestly",
            content: "Track cost per result against the profit per sale. If an ad costs more than it earns after fees, pause it. Let data, not hope, decide what scales.",
            voiceover: "Marketing is math with a creative coat on. Keep the math honest and the creativity pays off." },
        ],
        quiz: [
          q("How should beginners start with ads?", ["Max budget day one", "Small budget, one goal, one test", "No goal", "Copy a big brand"], 1),
          q("When should you pause an ad?", ["Never", "When it costs more than it earns after fees", "When it makes money", "Randomly"], 1),
        ],
      },
    ],
    finalAssessment: [
      q("Marketing should begin with…", ["a huge budget", "a clear offer, audience, and message", "a mascot", "a discount"], 1),
      q("Content builds trust when it is…", ["pure hard-sell", "helpful and honest", "rare", "copied"], 1),
      q("A healthy email list is based on…", ["permission", "purchased lists", "spam", "luck"], 0),
      q("The safest way to start advertising is…", ["small, measured tests", "spend everything", "no tracking", "one giant campaign"], 0),
      q("You should scale an ad when…", ["it loses money", "it earns more than it costs after fees", "you feel like it", "never"], 1),
    ],
  },

  {
    slug: "customer-service",
    title: "Customer Service Excellence",
    category: "Operations & Compliance",
    level: "Beginner",
    duration: "2h 20m",
    languages: ["English", "Français", "Kreyòl", "Español"],
    rich: true,
    instructor: "priya",
    summary: "Turn buyers into loyal fans with fast, empathetic, and consistent support that protects your reputation.",
    description: "Support is where trust is won or lost. This course gives you a simple system for responding quickly, communicating with empathy, resolving issues fairly, and turning reviews and complaints into repeat business.",
    objectives: [
      "Respond quickly and set clear response-time expectations",
      "Use empathetic, professional language in every reply",
      "Resolve common issues fairly with a repeatable framework",
      "Turn feedback and reviews into loyalty and improvement",
    ],
    i18n: {
      fr: { title: "Excellence du Service Client", summary: "Transformez vos acheteurs en fans fidèles grâce à un support rapide, empathique et cohérent.",
        objectives: ["Répondre vite et fixer des délais clairs", "Utiliser un langage empathique et professionnel", "Résoudre les problèmes équitablement", "Transformer les avis en fidélité"] },
      ht: { title: "Eksèlans Sèvis Kliyan", summary: "Transfòme achtè yo an fanatik fidèl ak yon sèvis rapid, ki gen empati, epi konsistan.",
        objectives: ["Reponn vit epi fikse dat repons klè", "Itilize langaj ki gen empati epi pwofesyonèl", "Rezoud pwoblèm ak jistis", "Transfòme fidbak an fidelite"] },
      es: { title: "Excelencia en Servicio al Cliente", summary: "Convierte compradores en fans leales con soporte rápido, empático y consistente.",
        objectives: ["Responder rápido y fijar tiempos claros", "Usar lenguaje empático y profesional", "Resolver problemas con justicia", "Convertir opiniones en lealtad"] },
    },
    resources: [
      { title: "Reply templates (5 common cases)", type: "Doc" },
      { title: "Refund & return decision guide", type: "PDF" },
      { title: "Review response cheat sheet", type: "Sheet" },
    ],
    modules: [
      {
        title: "Speed & First Impressions",
        avatar: "priya",
        avatarLine: "I'm Priya. The first reply sets the tone for everything — let's make it count.",
        video: { title: "Module 1 Overview", duration: "4:40" },
        lessons: [
          { title: "Why response time matters",
            content: "A fast first reply — even just to acknowledge — reassures customers you're on it. Set a target response window and publish it so expectations are clear.",
            voiceover: "Silence feels like being ignored. A quick 'we're on it' buys you time and goodwill." },
          { title: "Setting expectations",
            content: "Tell customers when to expect a full answer. Meeting a stated timeline builds more trust than a fast promise you can't keep.",
            voiceover: "Say when you'll follow up, then do it. Reliability is a feeling, and it's built on kept promises." },
        ],
        quiz: [
          q("Why is a fast first reply important?", ["It's not", "It reassures the customer you're handling it", "To close the ticket", "To avoid work"], 1),
          q("Trust grows most when you…", ["over-promise", "meet the timeline you stated", "stay silent", "reply once a week"], 1),
        ],
      },
      {
        title: "Empathy & Language",
        avatar: "priya",
        avatarLine: "How you say it matters as much as what you say. Words can calm or inflame.",
        video: { title: "Module 2 Overview", duration: "5:00" },
        lessons: [
          { title: "Empathetic phrasing",
            content: "Acknowledge the feeling first ('I understand how frustrating that is'), then move to the fix. People need to feel heard before they can hear a solution.",
            voiceover: "Feelings first, facts second. A frustrated customer who feels understood becomes far easier to help." },
          { title: "Staying professional under pressure",
            content: "Never match anger with anger. Stay calm, use the customer's name, and keep the focus on the solution, not on winning the argument.",
            voiceover: "You can't lose an argument you refuse to have. Calm is a superpower in support." },
        ],
        quiz: [
          q("What should you address first in a complaint?", ["The refund policy", "The customer's feeling", "Your terms", "Nothing"], 1),
          q("When a customer is angry, you should…", ["match their anger", "stay calm and focus on the fix", "ignore them", "argue to win"], 1),
        ],
      },
      {
        title: "Resolving Issues Fairly",
        avatar: "priya",
        avatarLine: "A simple framework keeps your decisions fair, fast, and consistent.",
        video: { title: "Module 3 Overview", duration: "5:10" },
        lessons: [
          { title: "The LEAF framework",
            content: "Listen, Empathize, Act, Follow-up. Understand the problem, acknowledge it, take a clear action (fix, replace, refund), then check back to confirm it's resolved.",
            voiceover: "LEAF keeps you from freezing under pressure: Listen, Empathize, Act, Follow-up. Same steps, every time." },
          { title: "Fair, consistent decisions",
            content: "Use clear internal guidelines for refunds and replacements so every customer is treated the same. Consistency prevents disputes and protects your reputation.",
            voiceover: "Fairness isn't just kind — it's efficient. Consistent rules mean fewer arguments and faster resolutions." },
        ],
        quiz: [
          q("What does LEAF stand for?", ["Listen, Empathize, Act, Follow-up", "Look, Eat, Ask, Fix", "Leave Every Angry Feedback", "List, Email, Add, Finish"], 0),
          q("Consistent refund guidelines help by…", ["confusing customers", "treating everyone fairly and reducing disputes", "slowing you down", "raising prices"], 1),
        ],
      },
      {
        title: "Turning Feedback Into Loyalty",
        avatar: "priya",
        avatarLine: "Every review — good or bad — is a chance to build your brand.",
        video: { title: "Module 4 Overview", duration: "4:30" },
        lessons: [
          { title: "Responding to reviews",
            content: "Thank positive reviewers warmly. For negative ones, respond publicly with empathy and a path to fix it. Onlookers judge you by how you handle criticism.",
            voiceover: "A calm, caring reply to a bad review can win more customers than the review lost. Everyone's watching how you respond." },
          { title: "Closing the loop",
            content: "Use recurring complaints to improve products and listings. When you fix a root cause, tell the customer — it turns a critic into a champion.",
            voiceover: "Feedback is free product research. Fix the cause, close the loop, and watch loyalty grow." },
        ],
        quiz: [
          q("How should you handle a negative review?", ["Delete it", "Respond publicly with empathy and a fix", "Argue", "Ignore it"], 1),
          q("Recurring complaints are best used to…", ["punish customers", "improve products and listings", "raise prices", "close the store"], 1),
        ],
      },
    ],
    finalAssessment: [
      q("A fast first reply mainly…", ["closes tickets", "reassures the customer you're handling it", "avoids work", "raises prices"], 1),
      q("In a complaint, address the customer's ___ first.", ["policy", "feeling", "invoice", "address"], 1),
      q("LEAF stands for Listen, Empathize, Act, and…", ["Finish", "Follow-up", "Forget", "File"], 1),
      q("Consistent guidelines make decisions…", ["random", "fair and dispute-reducing", "slower", "hidden"], 1),
      q("The smart way to handle a bad review is to…", ["delete it", "reply with empathy and a fix", "argue", "ignore it"], 1),
    ],
  },

  {
    slug: "financial-literacy",
    title: "Financial Literacy for Entrepreneurs",
    category: "Business Foundations",
    level: "Beginner",
    duration: "2h 35m",
    languages: ["English", "Français", "Kreyòl", "Español"],
    rich: true,
    instructor: "david",
    summary: "Understand the numbers that keep a business alive — cash flow, profit, pricing, and simple record-keeping.",
    description: "You don't need to be an accountant to run a healthy business, but you do need to understand a few key numbers. This course demystifies cash flow vs profit, margins, break-even, and simple bookkeeping so you can make confident money decisions.",
    objectives: [
      "Tell the difference between cash flow and profit",
      "Calculate margin, markup, and break-even",
      "Keep simple, reliable records of money in and out",
      "Make pricing and spending decisions grounded in numbers",
    ],
    i18n: {
      fr: { title: "Culture Financière pour Entrepreneurs", summary: "Comprenez les chiffres qui maintiennent une entreprise en vie — trésorerie, profit, prix et comptabilité simple.",
        objectives: ["Distinguer trésorerie et profit", "Calculer marge, majoration et seuil de rentabilité", "Tenir des registres simples et fiables", "Décider des prix et dépenses à partir des chiffres"] },
      ht: { title: "Konesans Finansye pou Antreprenè", summary: "Konprann chif ki kenbe yon biznis vivan — kach, pwofi, pri, ak senp kontablite.",
        objectives: ["Fè diferans ant kach ak pwofi", "Kalkile maj, majorasyon, ak pwen egalite", "Kenbe rejis senp epi serye", "Deside pri ak depans ak chif"] },
      es: { title: "Educación Financiera para Emprendedores", summary: "Entiende los números que mantienen vivo un negocio — flujo de caja, ganancia, precios y registros simples.",
        objectives: ["Diferenciar flujo de caja y ganancia", "Calcular margen, margen de ganancia y punto de equilibrio", "Llevar registros simples y confiables", "Decidir precios y gastos con base en números"] },
    },
    resources: [
      { title: "Cash-flow tracker template", type: "Sheet" },
      { title: "Margin & break-even calculator", type: "Sheet" },
      { title: "Monthly money review checklist", type: "PDF" },
    ],
    modules: [
      {
        title: "Cash Flow vs Profit",
        avatar: "david",
        avatarLine: "I'm David. This one distinction saves more businesses than any other — let's nail it.",
        video: { title: "Module 1 Overview", duration: "5:30" },
        lessons: [
          { title: "The critical difference",
            content: "Profit is what's left after all costs over a period. Cash flow is the actual money moving in and out day to day. A profitable business can still fail if it runs out of cash to pay bills on time.",
            voiceover: "Profit is an opinion at month-end; cash is a fact today. You pay rent with cash, not with profit on paper." },
          { title: "Why timing matters",
            content: "Money often goes out before it comes in — you pay suppliers now and get paid later. Managing that gap is the heart of survival, especially when growing fast.",
            voiceover: "Growth eats cash. The faster you grow, the more carefully you must watch the timing of money in versus out." },
        ],
        quiz: [
          q("Profit is…", ["money moving daily", "what's left after all costs over a period", "your bank balance", "revenue"], 1),
          q("A profitable business can still fail if it…", ["has too much cash", "runs out of cash to pay bills on time", "grows slowly", "keeps records"], 1),
        ],
      },
      {
        title: "Margins & Break-Even",
        avatar: "david",
        avatarLine: "A few simple calculations tell you if a product is worth selling at all.",
        video: { title: "Module 2 Overview", duration: "5:45" },
        lessons: [
          { title: "Margin vs markup",
            content: "Markup is added on top of cost; margin is profit as a percentage of the selling price. If an item costs $10 and sells for $15, markup is 50% but margin is about 33%. Know which one you're quoting.",
            voiceover: "People confuse these constantly and lose money. Margin is always measured against the price the customer pays." },
          { title: "Finding break-even",
            content: "Break-even is the number of sales needed to cover your costs. Divide your fixed costs by the profit per unit. Below it you lose money; above it you profit.",
            voiceover: "Know your break-even before you launch. It turns 'I hope this works' into 'I need X sales to win.'" },
        ],
        quiz: [
          q("Margin is profit as a percentage of…", ["cost", "the selling price", "markup", "tax"], 1),
          q("Break-even = fixed costs divided by…", ["revenue", "profit per unit", "tax rate", "number of products"], 1),
        ],
      },
      {
        title: "Simple Record-Keeping",
        avatar: "david",
        avatarLine: "You don't need fancy software — you need a habit you'll actually keep.",
        video: { title: "Module 3 Overview", duration: "4:50" },
        lessons: [
          { title: "Money in, money out",
            content: "Record every sale and every expense, ideally the same day. A simple sheet with date, description, in, and out is enough to start and prevents nasty surprises.",
            voiceover: "The best system is the one you'll use. A tidy simple sheet beats a perfect system you abandon." },
          { title: "Separating business and personal",
            content: "Keep a separate account for the business. Mixing personal and business money hides your true numbers and creates headaches at tax time.",
            voiceover: "One wallet for the business, one for you. Clean lines today save you hours and stress later." },
        ],
        quiz: [
          q("When is the best time to record a transaction?", ["Once a year", "Ideally the same day", "Never", "Only big ones"], 1),
          q("Why separate business and personal money?", ["It looks nice", "It reveals true numbers and eases taxes", "It's required to sell", "No reason"], 1),
        ],
      },
      {
        title: "Making Money Decisions",
        avatar: "david",
        avatarLine: "Numbers should guide your choices — not fear, not hype.",
        video: { title: "Module 4 Overview", duration: "5:00" },
        lessons: [
          { title: "Pricing with confidence",
            content: "Set prices from your costs and target margin, then check the market. If the market won't pay a profitable price, change the product or costs — don't sell at a loss hoping for volume.",
            voiceover: "Price from your numbers first, then sanity-check the market. Volume never fixes a losing margin." },
          { title: "Spending that pays back",
            content: "Judge each expense by whether it earns more than it costs (its return). Prioritize spending that directly helps you sell or serve customers better.",
            voiceover: "Every dollar should have a job. If a cost doesn't help you sell or serve, question it." },
        ],
        quiz: [
          q("You should set prices primarily from…", ["a guess", "your costs and target margin, checked against market", "competitors only", "the weather"], 1),
          q("The best expenses are ones that…", ["feel nice", "earn back more than they cost", "are the cheapest", "impress friends"], 1),
        ],
      },
    ],
    finalAssessment: [
      q("Cash flow is…", ["profit on paper", "actual money moving in and out day to day", "revenue", "your margin"], 1),
      q("Margin is measured against the…", ["cost", "selling price", "markup", "tax"], 1),
      q("Break-even equals fixed costs divided by…", ["profit per unit", "revenue", "tax", "units in stock"], 0),
      q("The best time to record transactions is…", ["yearly", "the same day", "never", "only big ones"], 1),
      q("Prices should be set primarily from…", ["a guess", "costs and target margin, checked vs market", "competitors only", "hope"], 1),
    ],
  },

  // ===== Batch 2 — 8 fully authored courses =====
  {
    slug: "dropshipping-foundations",
    title: "Dropshipping Foundations",
    category: "Commerce & Marketplace",
    level: "Beginner",
    duration: "2h 10m",
    languages: ["English", "Français", "Kreyòl", "Español"],
    rich: true,
    instructor: "amara",
    summary: "The absolute beginner's start to dropshipping — what it is, how money flows, and the first steps to launch safely.",
    description: "This is your day-one guide to dropshipping. Before advanced tactics, you'll build a solid mental model: how the business really works, where your profit comes from, the mistakes that sink beginners, and a simple checklist to launch your first store the right way — all in plain, beginner-friendly language.",
    objectives: [
      "Describe the dropshipping business model in simple terms",
      "Identify where profit comes from and the costs that reduce it",
      "Avoid the most common beginner mistakes",
      "Follow a clear checklist to launch your first store safely",
    ],
    i18n: {
      fr: { title: "Bases du Dropshipping", summary: "Le point de départ pour débutants — ce que c'est, comment l'argent circule, et les premières étapes pour lancer en toute sécurité.",
        objectives: ["Décrire le modèle du dropshipping simplement", "Identifier d'où vient le profit et les coûts qui le réduisent", "Éviter les erreurs courantes des débutants", "Suivre une checklist claire pour lancer sa boutique"] },
      ht: { title: "Baz Dropshipping", summary: "Kote pou debitan kòmanse — kisa li ye, kijan lajan sikile, ak premye etap pou lanse an sekirite.",
        objectives: ["Dekri modèl dropshipping an senp", "Idantifye kote pwofi soti ak kòb ki redwi li", "Evite erè debitan yo fè souvan", "Swiv yon lis klè pou lanse boutik ou"] },
      es: { title: "Fundamentos del Dropshipping", summary: "El punto de partida para principiantes — qué es, cómo fluye el dinero y los primeros pasos para lanzar con seguridad.",
        objectives: ["Describir el modelo de dropshipping de forma simple", "Identificar de dónde viene la ganancia y qué costos la reducen", "Evitar los errores comunes de principiantes", "Seguir una lista clara para lanzar tu tienda"] },
    },
    resources: [
      { title: "First-store launch checklist", type: "PDF" },
      { title: "Beginner profit math worksheet", type: "Sheet" },
      { title: "Common-mistakes quick guide", type: "Doc" },
    ],
    modules: [
      {
        title: "What Dropshipping Is",
        avatar: "amara",
        avatarLine: "Hi, I'm Amara. Let's start at zero — by the end of this module the whole model will click.",
        video: { title: "Module 1 Overview", duration: "5:00" },
        lessons: [
          { title: "The simple picture",
            content: "You open a store and list products. When someone buys, you order that product from your supplier, who ships it straight to your customer. You never touch inventory — you focus on choosing products, marketing, and service.",
            voiceover: "Picture a friendly middle-person: you connect a customer to a great product, the supplier ships the box, and you keep the difference. That's dropshipping in one breath." },
          { title: "Why beginners choose it",
            content: "It has a low startup cost and no risk of unsold stock. That makes it a popular first business — but low cost does not mean no effort. Your edge comes from better choices and better service.",
            voiceover: "Low barrier to start, real work to succeed. The store that helps customers most is the one that wins." },
        ],
        quiz: [
          q("In dropshipping you hold your own inventory. True or false?", ["True", "False", "Only sometimes", "Only for big items"], 1),
          q("Where does your profit come from?", ["Government grants", "The gap between customer price and supplier cost, minus fees", "Ad platforms", "Free shipping"], 1),
        ],
      },
      {
        title: "Money Flow & Costs",
        avatar: "amara",
        avatarLine: "Understanding the money flow keeps you from the #1 beginner trap: selling at a loss.",
        video: { title: "Module 2 Overview", duration: "5:20" },
        lessons: [
          { title: "Following the money",
            content: "The customer pays you first. You then pay the supplier and any platform or payment fees. What remains is your profit. If you don't count every cost, you can 'sell a lot' and still lose money.",
            voiceover: "Cash comes in, costs go out, profit is what's left. Count every cost before you celebrate a sale." },
          { title: "The costs beginners forget",
            content: "Payment processing fees, shipping, ad spend, and refunds all eat into profit. Build these into your price from the start so every sale leaves a healthy margin.",
            voiceover: "Fees are quiet but hungry. Price with them in mind and your margin stays safe." },
        ],
        quiz: [
          q("Who pays first in a dropshipping order?", ["The supplier", "The customer", "The ad platform", "TAVEN"], 1),
          q("Which is a cost beginners often forget?", ["Payment & shipping fees", "The color of the box", "Their name", "The weather"], 0),
        ],
      },
      {
        title: "Avoiding Beginner Mistakes",
        avatar: "amara",
        avatarLine: "Let's skip the painful lessons — here are the traps that catch almost everyone.",
        video: { title: "Module 3 Overview", duration: "4:40" },
        lessons: [
          { title: "The top three traps",
            content: "Selling before testing a sample, pricing too low to profit, and hiding shipping times. Each one destroys trust or margin. Test, price with margin, and be honest about delivery.",
            voiceover: "Test it, price it right, tell the truth about timing. Do those three and you're ahead of most beginners." },
          { title: "Building trust from day one",
            content: "Clear photos, honest descriptions, visible shipping windows, and fast replies make a new store feel safe. Trust is what turns a curious visitor into a buyer.",
            voiceover: "New stores earn trust with clarity and speed. Look reliable and customers will take the chance on you." },
        ],
        quiz: [
          q("What should you do before selling a product?", ["Test a sample", "Raise the price", "Hide reviews", "Nothing"], 0),
          q("What helps a new store feel trustworthy?", ["Hidden shipping times", "Clear photos, honest info, fast replies", "No contact info", "Fake reviews"], 1),
        ],
      },
      {
        title: "Launch Your First Store",
        avatar: "amara",
        avatarLine: "Now let's turn knowledge into action with a simple, safe launch plan.",
        video: { title: "Module 4 Overview", duration: "5:10" },
        lessons: [
          { title: "The launch checklist",
            content: "Pick one product you've tested, write an honest listing with clear photos, set a price with margin, state your shipping window, and prepare a simple reply for common questions. Start small and learn.",
            voiceover: "One good product, one honest listing, one clear promise. A focused start beats a messy megastore." },
          { title: "Your first customers",
            content: "Share with people who'd genuinely want it, ask for honest feedback, and respond quickly. Early reviews and word of mouth are worth more than any ad in the beginning.",
            voiceover: "Your first fans are gold. Serve them well and they'll bring the next ten." },
        ],
        quiz: [
          q("A safe first launch focuses on…", ["one tested product", "fifty products at once", "no photos", "no pricing"], 0),
          q("In the beginning, what's most valuable?", ["Expensive ads", "Happy early customers and honest reviews", "Hiding from customers", "Random discounts"], 1),
        ],
      },
    ],
    finalAssessment: [
      q("Dropshipping means you…", ["manufacture products", "sell without holding inventory", "only sell locally", "never market"], 1),
      q("Your profit is…", ["the full sale price", "customer price minus supplier cost and fees", "the ad budget", "zero"], 1),
      q("A common beginner trap is…", ["testing samples", "pricing too low to profit", "being honest", "replying fast"], 1),
      q("Trust in a new store is built with…", ["hidden info", "clarity, honesty, and fast replies", "fake reviews", "no contact"], 1),
      q("A safe launch starts with…", ["one tested product", "hundreds of products", "no pricing plan", "no photos"], 0),
    ],
  },

  {
    slug: "product-research",
    title: "Product Research & Winning Products",
    category: "Commerce & Marketplace",
    level: "Beginner",
    duration: "2h 30m",
    languages: ["English", "Français", "Kreyòl", "Español"],
    rich: true,
    instructor: "david",
    summary: "Find products people actually want — using demand signals, simple validation, and honest margin math instead of guessing.",
    description: "Great stores are built on great product choices. This course gives you a repeatable research method: how to spot real demand, read simple signals, validate before you commit, and confirm the numbers work — so you stop guessing and start choosing winners.",
    objectives: [
      "Use practical signals to gauge real product demand",
      "Validate a product idea before investing time or money",
      "Judge whether a product's margins can actually work",
      "Build a simple, repeatable product-research routine",
    ],
    i18n: {
      fr: { title: "Recherche de Produits Gagnants", summary: "Trouvez des produits vraiment désirés — grâce aux signaux de demande, à la validation simple et au calcul honnête des marges.",
        objectives: ["Utiliser des signaux pratiques pour évaluer la demande", "Valider une idée avant d'investir", "Juger si les marges d'un produit fonctionnent", "Bâtir une routine de recherche répétable"] },
      ht: { title: "Rechèch Pwodwi k ap Genyen", summary: "Jwenn pwodwi moun vle vre — ak siyal demann, validasyon senp, ak kalkil maj onèt.",
        objectives: ["Itilize siyal pratik pou mezire demann", "Valide yon lide anvan ou envesti", "Jije si maj yon pwodwi ka mache", "Bati yon woutin rechèch ou ka repete"] },
      es: { title: "Investigación de Productos Ganadores", summary: "Encuentra productos que la gente realmente quiere — con señales de demanda, validación simple y cálculo honesto de márgenes.",
        objectives: ["Usar señales prácticas para medir la demanda", "Validar una idea antes de invertir", "Juzgar si los márgenes de un producto funcionan", "Crear una rutina de investigación repetible"] },
    },
    resources: [
      { title: "Product validation scorecard", type: "PDF" },
      { title: "Demand-signals checklist", type: "Doc" },
      { title: "Margin feasibility calculator", type: "Sheet" },
    ],
    modules: [
      {
        title: "What Makes a Winning Product",
        avatar: "david",
        avatarLine: "I'm David. Winners aren't lucky guesses — they share clear traits. Let's learn to spot them.",
        video: { title: "Module 1 Overview", duration: "5:30" },
        lessons: [
          { title: "The traits of winners",
            content: "Winning products usually solve a clear problem, spark an emotion or 'wow', are hard to find in local stores, and are light and durable to ship. Tick several of these and you have a strong candidate.",
            voiceover: "Problem-solving, a little wow, not on every corner, and easy to ship. Stack those traits and you're onto something." },
          { title: "Avoiding weak picks",
            content: "Skip fragile, oversized, heavily branded, or restricted items. They cause returns, high shipping costs, and legal headaches. A calmer product with steady demand beats a risky viral gamble.",
            voiceover: "Some products look exciting but bleed you with returns and shipping. Boring-but-profitable pays the bills." },
        ],
        quiz: [
          q("A winning product usually…", ["is fragile and huge", "solves a clear problem and ships easily", "is illegal", "has no demand"], 1),
          q("Which product type should beginners avoid?", ["Lightweight and useful", "Fragile, oversized, or restricted", "Problem-solving", "Giftable"], 1),
        ],
      },
      {
        title: "Reading Demand Signals",
        avatar: "david",
        avatarLine: "You don't need expensive tools — you need to read the signals that are already out there.",
        video: { title: "Module 2 Overview", duration: "5:40" },
        lessons: [
          { title: "Where demand shows up",
            content: "Look at how often people search for a product, how much it's talked about on social platforms, and whether similar items have many reviews. Consistent interest over time beats a one-day spike.",
            voiceover: "Demand leaves footprints: searches, comments, reviews. Follow steady footprints, not a single flash." },
          { title: "Signal vs noise",
            content: "A viral moment can fade fast. Favor products with steady, repeatable demand and room for you to add value — better photos, clearer info, or faster service.",
            voiceover: "Trends are fun, but stability is a business. Ask: will people still want this next month?" },
        ],
        quiz: [
          q("A stronger demand signal is…", ["one viral day", "consistent interest over time", "no reviews", "a single comment"], 1),
          q("Steady demand is valuable because…", ["it fades instantly", "it supports a repeatable business", "it's illegal", "it needs no service"], 1),
        ],
      },
      {
        title: "Validate Before You Commit",
        avatar: "david",
        avatarLine: "Cheap validation now saves expensive mistakes later. Let's test smart.",
        video: { title: "Module 3 Overview", duration: "5:00" },
        lessons: [
          { title: "Small, safe tests",
            content: "Order a sample, gauge interest with a simple post or small ad, and note real reactions. Let a little real-world feedback confirm the idea before you build a whole store around it.",
            voiceover: "Test before you invest. A small signal from real people is worth more than your own hopeful hunch." },
          { title: "Reading your results",
            content: "Interest with a workable price and margin is a green light. Weak interest or impossible margins is a signal to move on quickly — without emotion.",
            voiceover: "Let data, not attachment, decide. Killing a weak idea early is a win, not a loss." },
        ],
        quiz: [
          q("A smart way to validate is…", ["build a giant store first", "a small sample and small test", "ignore feedback", "guess"], 1),
          q("Weak interest and impossible margins mean…", ["push harder blindly", "move on quickly", "raise the price forever", "hide the results"], 1),
        ],
      },
      {
        title: "Do the Margin Math",
        avatar: "david",
        avatarLine: "A product only wins if the numbers win. Let's confirm it can actually profit.",
        video: { title: "Module 4 Overview", duration: "5:15" },
        lessons: [
          { title: "Landed cost first",
            content: "Add product cost, shipping, and fees to get your landed cost. Your selling price must comfortably exceed this and still be competitive in the market.",
            voiceover: "Start from landed cost, always. If the market price can't clear it with margin, it's not a winner — no matter how cool it looks." },
          { title: "Using TAVEN Price Guard",
            content: "TAVEN's Price Guard can flag when a price is too low to be profitable or too high for the market. Use it as a sanity check so your winning product stays a winning business.",
            voiceover: "Let Price Guard be your second opinion. A great product at a losing price is still a loss." },
        ],
        quiz: [
          q("Landed cost includes product cost, shipping, and…", ["fees", "nothing", "your rent", "taxes only"], 0),
          q("A product is a real winner only if…", ["it looks cool", "the numbers profit after all costs", "it's cheapest", "it's viral"], 1),
        ],
      },
    ],
    finalAssessment: [
      q("Winning products usually…", ["are fragile and huge", "solve a problem and ship easily", "have no demand", "are restricted"], 1),
      q("The best demand signal is…", ["one viral day", "steady interest over time", "no reviews", "a guess"], 1),
      q("Smart validation uses…", ["a giant store first", "a small sample and test", "no feedback", "emotion"], 1),
      q("Landed cost is product cost plus shipping and…", ["fees", "rent", "salary", "nothing"], 0),
      q("A product wins only when…", ["it looks cool", "the numbers profit after all costs", "it's the cheapest", "it's trendy"], 1),
    ],
  },

  {
    slug: "supplier-management",
    title: "Supplier Management",
    category: "Operations & Compliance",
    level: "Beginner",
    duration: "2h 15m",
    languages: ["English", "Français", "Kreyòl", "Español"],
    rich: true,
    instructor: "amara",
    summary: "Build reliable supplier relationships — vet partners, communicate clearly, protect quality, and avoid costly disruptions.",
    description: "Your suppliers can make or break your store. This course shows you how to find and vet dependable suppliers, communicate professionally, agree on clear terms, safeguard quality, and manage risk so late shipments and quality slips don't damage your reputation.",
    objectives: [
      "Find and vet reliable suppliers with a repeatable process",
      "Communicate expectations and terms clearly and professionally",
      "Protect product quality with simple checks",
      "Reduce risk by planning for delays and backups",
    ],
    i18n: {
      fr: { title: "Gestion des Fournisseurs", summary: "Construisez des relations fiables — évaluez les partenaires, communiquez clairement, protégez la qualité et évitez les ruptures coûteuses.",
        objectives: ["Trouver et évaluer des fournisseurs fiables", "Communiquer clairement attentes et conditions", "Protéger la qualité avec des vérifications simples", "Réduire le risque en planifiant les retards"] },
      ht: { title: "Jesyon Founisè", summary: "Bati relasyon serye — evalye patnè, kominike klè, pwoteje kalite, epi evite dezòd ki koute chè.",
        objectives: ["Jwenn epi evalye founisè serye", "Kominike atant ak kondisyon klè", "Pwoteje kalite ak verifikasyon senp", "Redwi risk lè w planifye reta"] },
      es: { title: "Gestión de Proveedores", summary: "Construye relaciones confiables — evalúa socios, comunica con claridad, protege la calidad y evita interrupciones costosas.",
        objectives: ["Encontrar y evaluar proveedores confiables", "Comunicar expectativas y términos con claridad", "Proteger la calidad con verificaciones simples", "Reducir el riesgo planificando los retrasos"] },
    },
    resources: [
      { title: "Supplier vetting scorecard", type: "PDF" },
      { title: "Supplier communication templates", type: "Doc" },
      { title: "Quality-check checklist", type: "Sheet" },
    ],
    modules: [
      {
        title: "Finding & Vetting Suppliers",
        avatar: "amara",
        avatarLine: "Hi, I'm Amara. A great supplier is a partner, not just a price. Let's find the right ones.",
        video: { title: "Module 1 Overview", duration: "5:10" },
        lessons: [
          { title: "What to check first",
            content: "Order a sample, measure response time, and review their processing and shipping speed. A supplier who answers quickly and ships on time is worth more than one who is slightly cheaper.",
            voiceover: "Speed of reply and quality of the sample tell you almost everything. Cheap-but-slow costs you customers." },
          { title: "Red flags to avoid",
            content: "Watch for vague answers, no samples, inconsistent pricing, and poor communication. These often signal problems that will land on your customers — and your reviews.",
            voiceover: "If they're hard to reach before you're a customer, imagine how hard when there's a problem. Trust the early signals." },
        ],
        quiz: [
          q("The best first step in vetting a supplier is…", ["sign a huge order", "order a sample and test response time", "ignore communication", "pick the cheapest"], 1),
          q("A supplier red flag is…", ["fast replies", "clear pricing", "vague answers and poor communication", "offering samples"], 2),
        ],
      },
      {
        title: "Clear Communication & Terms",
        avatar: "amara",
        avatarLine: "Most supplier problems are really communication problems. Let's prevent them.",
        video: { title: "Module 2 Overview", duration: "5:00" },
        lessons: [
          { title: "Setting expectations in writing",
            content: "Agree on price, processing time, shipping method, and how issues are handled — in writing. Written terms prevent misunderstandings and give you something to reference calmly if things slip.",
            voiceover: "Put it in writing, kindly and clearly. Clarity today prevents arguments tomorrow." },
          { title: "Staying professional",
            content: "Be polite, specific, and consistent. A respectful, reliable buyer often gets better service, faster fixes, and priority when stock is tight.",
            voiceover: "Professionalism is leverage. Suppliers protect the buyers who treat them well and communicate clearly." },
        ],
        quiz: [
          q("Terms with a supplier should be…", ["only verbal", "agreed clearly in writing", "left vague", "never discussed"], 1),
          q("A professional buyer tends to get…", ["worse service", "better service and faster fixes", "no replies", "higher prices only"], 1),
        ],
      },
      {
        title: "Protecting Quality",
        avatar: "amara",
        avatarLine: "Your name is on every order. Let's keep quality consistent.",
        video: { title: "Module 3 Overview", duration: "4:50" },
        lessons: [
          { title: "Simple quality checks",
            content: "Re-check samples periodically, read your customer reviews for quality clues, and ask about any change in materials or packaging. Small checks catch slips before they become refund waves.",
            voiceover: "Quality drifts quietly. Regular small checks keep your standards — and your reviews — high." },
          { title: "Acting on quality issues",
            content: "If quality drops, raise it clearly with the supplier, request a fix, and be ready to switch if it continues. Never let a supplier's slip become your customer's problem.",
            voiceover: "Defend your customers. A supplier who won't fix quality isn't worth the discount." },
        ],
        quiz: [
          q("A good way to protect quality is to…", ["never check again", "re-check samples and read reviews", "ignore packaging", "hide complaints"], 1),
          q("If a supplier's quality keeps dropping, you should…", ["ignore it", "raise it and be ready to switch", "blame customers", "raise your price"], 1),
        ],
      },
      {
        title: "Managing Risk & Backups",
        avatar: "amara",
        avatarLine: "Smart sellers plan for the day something goes wrong — because eventually it will.",
        video: { title: "Module 4 Overview", duration: "4:55" },
        lessons: [
          { title: "Planning for delays",
            content: "Know your supplier's busy seasons and typical delays, and build a little buffer into your promised delivery windows. Communicate proactively when timelines shift.",
            voiceover: "Delays happen; surprises hurt. A buffer and a heads-up keep customers calm and loyal." },
          { title: "Having a backup supplier",
            content: "Identify a second reliable supplier for your key products. If your main partner runs out or slips, a backup keeps your store running and your customers served.",
            voiceover: "One supplier is a single point of failure. A tested backup is cheap insurance for your whole business." },
        ],
        quiz: [
          q("A smart way to handle delays is to…", ["hide them", "build a buffer and communicate early", "cancel silently", "blame the customer"], 1),
          q("Why keep a backup supplier?", ["To confuse customers", "To keep the store running if the main one slips", "No reason", "To raise prices"], 1),
        ],
      },
    ],
    finalAssessment: [
      q("The best first step in vetting a supplier is…", ["a huge order", "a sample and response-time test", "ignoring them", "picking cheapest"], 1),
      q("Supplier terms should be…", ["vague", "clear and in writing", "verbal only", "skipped"], 1),
      q("To protect quality you should…", ["never check", "re-check samples and read reviews", "hide complaints", "ignore packaging"], 1),
      q("Planning for delays means…", ["hiding them", "buffers and proactive communication", "blaming customers", "silent cancels"], 1),
      q("A backup supplier helps you…", ["confuse buyers", "keep serving customers if the main slips", "raise prices", "do nothing"], 1),
    ],
  },

  {
    slug: "brand-building",
    title: "Branding for E-Commerce",
    category: "Marketing & Growth",
    level: "Beginner",
    duration: "2h 25m",
    languages: ["English", "Français", "Kreyòl", "Español"],
    rich: true,
    instructor: "marcus",
    summary: "Turn a store into a brand people remember and trust — with a clear identity, consistent voice, and a memorable experience.",
    description: "Anyone can list products; brands earn loyalty and premium prices. This course teaches the foundations of branding for online stores: defining your identity and promise, creating a consistent look and voice, and designing an experience that makes customers choose you again and tell their friends.",
    objectives: [
      "Define a clear brand identity, promise, and audience",
      "Create a consistent visual look and written voice",
      "Design a memorable, trust-building customer experience",
      "Use branding to earn loyalty and support better prices",
    ],
    i18n: {
      fr: { title: "Image de Marque pour E-Commerce", summary: "Transformez une boutique en marque mémorable et fiable — identité claire, voix cohérente et expérience marquante.",
        objectives: ["Définir une identité, une promesse et une audience claires", "Créer un look visuel et une voix cohérents", "Concevoir une expérience client mémorable", "Utiliser la marque pour fidéliser et mieux vendre"] },
      ht: { title: "Branding pou E-Commerce", summary: "Transfòme yon boutik an yon mak moun sonje epi fè konfyans — idantite klè, vwa konsistan, ak eksperyans ki make.",
        objectives: ["Defini yon idantite, pwomès, ak odyans klè", "Kreye yon look ak yon vwa konsistan", "Konsevwa yon eksperyans kliyan memorab", "Itilize mak pou fidelize epi vann pi byen"] },
      es: { title: "Marca para E-Commerce", summary: "Convierte una tienda en una marca memorable y confiable — identidad clara, voz consistente y experiencia inolvidable.",
        objectives: ["Definir una identidad, promesa y audiencia claras", "Crear un aspecto visual y una voz consistentes", "Diseñar una experiencia de cliente memorable", "Usar la marca para fidelizar y vender mejor"] },
    },
    resources: [
      { title: "Brand identity worksheet", type: "PDF" },
      { title: "Voice & tone guide template", type: "Doc" },
      { title: "Customer experience map", type: "Sheet" },
    ],
    modules: [
      {
        title: "What a Brand Really Is",
        avatar: "marcus",
        avatarLine: "I'm Marcus. A brand isn't a logo — it's the feeling people get when they think of you. Let's build that.",
        video: { title: "Module 1 Overview", duration: "5:20" },
        lessons: [
          { title: "Brand = promise + feeling",
            content: "Your brand is the promise you make and the feeling customers associate with you. A logo is just a symbol of that promise. Decide what you want to be known for before you design anything.",
            voiceover: "People don't remember logos; they remember how you made them feel. Start with the promise, not the pixels." },
          { title: "Knowing who it's for",
            content: "A brand can't be for everyone. Define your specific audience and what they value, so every choice — words, colors, products — speaks directly to them.",
            voiceover: "When you try to appeal to everyone, you connect with no one. Pick your people and speak their language." },
        ],
        quiz: [
          q("A brand is best described as…", ["just a logo", "a promise and the feeling it creates", "a price", "a warehouse"], 1),
          q("A strong brand is…", ["for everyone", "for a specific audience", "invisible", "random"], 1),
        ],
      },
      {
        title: "Look & Voice",
        avatar: "marcus",
        avatarLine: "Consistency is what makes a brand feel real. Let's lock in your look and voice.",
        video: { title: "Module 2 Overview", duration: "5:10" },
        lessons: [
          { title: "A consistent visual identity",
            content: "Choose a small set of colors, a couple of fonts, and a photo style — then use them everywhere. Consistency makes a small store look established and trustworthy.",
            voiceover: "Repetition builds recognition. The same colors and style everywhere make you look bigger and safer than you are." },
          { title: "Finding your voice",
            content: "Decide how you sound — warm, expert, playful, premium — and write everything in that tone. A consistent voice turns scattered posts into a recognizable personality.",
            voiceover: "Your voice is your brand talking. Keep it consistent and customers feel they know you." },
        ],
        quiz: [
          q("Visual consistency means using…", ["random colors each time", "the same colors, fonts, and style", "no images", "only text"], 1),
          q("Brand voice is…", ["how your brand sounds in words", "your logo", "your price", "your warehouse"], 0),
        ],
      },
      {
        title: "The Brand Experience",
        avatar: "marcus",
        avatarLine: "Every touchpoint is your brand. Let's make each one memorable.",
        video: { title: "Module 3 Overview", duration: "5:00" },
        lessons: [
          { title: "Touchpoints that matter",
            content: "Your product page, packaging note, confirmation message, and support replies all shape the brand. Small, thoughtful touches turn a transaction into an experience people remember.",
            voiceover: "The brand lives in the details: a kind note, a clear update, a fast reply. Sweat the small touches." },
          { title: "Consistency builds trust",
            content: "When the look, voice, and experience all match, customers relax and trust you. That trust is what earns repeat purchases and word-of-mouth referrals.",
            voiceover: "Aligned look, voice, and experience feel honest. Honesty, repeated, becomes trust." },
        ],
        quiz: [
          q("Which is a brand touchpoint?", ["Only the logo", "Product page, packaging, messages, and support", "Nothing customer-facing", "Just ads"], 1),
          q("Matching look, voice, and experience builds…", ["confusion", "trust and repeat business", "higher costs only", "nothing"], 1),
        ],
      },
      {
        title: "Branding That Pays Off",
        avatar: "marcus",
        avatarLine: "Done right, branding lets you compete on value, not just price.",
        video: { title: "Module 4 Overview", duration: "5:05" },
        lessons: [
          { title: "Loyalty over discounts",
            content: "A trusted brand keeps customers even when a cheaper option appears. Loyalty comes from consistent quality, voice, and care — not from constant discounting.",
            voiceover: "Discounts rent customers; brands keep them. Build loyalty and you stop competing on price alone." },
          { title: "Growing your reputation",
            content: "Encourage reviews, share customer stories, and keep your promises. A strong reputation becomes your best marketing — earned, trusted, and hard for rivals to copy.",
            voiceover: "Reputation is the marketing you can't buy. Keep promises and let happy customers speak for you." },
        ],
        quiz: [
          q("A trusted brand helps you…", ["only cut prices", "keep customers without constant discounts", "hide from buyers", "avoid quality"], 1),
          q("Your best long-term marketing is…", ["a strong, trusted reputation", "endless discounts", "no reviews", "copying rivals"], 0),
        ],
      },
    ],
    finalAssessment: [
      q("A brand is…", ["only a logo", "a promise and the feeling it creates", "a price tag", "a warehouse"], 1),
      q("A strong brand targets…", ["everyone", "a specific audience", "no one", "random people"], 1),
      q("Visual consistency uses…", ["random styles", "the same colors, fonts, and style", "no images", "text only"], 1),
      q("Brand experience includes…", ["only ads", "product page, packaging, messages, support", "nothing", "just the logo"], 1),
      q("Branding lets you compete on…", ["price only", "value and trust", "nothing", "luck"], 1),
    ],
  },

  {
    slug: "paid-ads-basics",
    title: "Paid Ads Basics",
    category: "Marketing & Growth",
    level: "Beginner",
    duration: "2h 20m",
    languages: ["English", "Français", "Kreyòl", "Español"],
    rich: true,
    instructor: "marcus",
    summary: "Run your first paid ads safely — understand how they work, start small, test smart, and read the numbers so you don't waste money.",
    description: "Paid ads can grow a store fast — or burn cash quickly. This beginner course demystifies how advertising works, how to build a simple ad, how to test with a small budget, and how to read results honestly so you scale winners and cut losers with confidence.",
    objectives: [
      "Understand how paid advertising works at a basic level",
      "Build a simple, clear ad around one offer",
      "Test with a small budget and one variable at a time",
      "Read results honestly to scale winners and cut losers",
    ],
    i18n: {
      fr: { title: "Bases de la Publicité Payante", summary: "Lancez vos premières pubs en sécurité — comprenez leur fonctionnement, commencez petit, testez malin et lisez les chiffres.",
        objectives: ["Comprendre le fonctionnement de base de la publicité", "Créer une pub simple autour d'une offre", "Tester avec un petit budget, une variable à la fois", "Lire les résultats honnêtement"] },
      ht: { title: "Baz Piblisite Peye", summary: "Fè premye pib ou an sekirite — konprann kijan yo mache, kòmanse piti, teste entelijan, epi li chif yo.",
        objectives: ["Konprann baz kijan piblisite mache", "Kreye yon pib senp otou yon òf", "Teste ak yon ti bidjè, yon varyab alafwa", "Li rezilta yo onètman"] },
      es: { title: "Fundamentos de Anuncios Pagados", summary: "Lanza tus primeros anuncios con seguridad — entiende cómo funcionan, empieza pequeño, prueba con inteligencia y lee los números.",
        objectives: ["Entender cómo funciona la publicidad pagada", "Crear un anuncio simple sobre una oferta", "Probar con poco presupuesto, una variable a la vez", "Leer los resultados con honestidad"] },
    },
    resources: [
      { title: "First-ad planning sheet", type: "PDF" },
      { title: "Small-budget test framework", type: "Doc" },
      { title: "Ad results tracker", type: "Sheet" },
    ],
    modules: [
      {
        title: "How Paid Ads Work",
        avatar: "marcus",
        avatarLine: "I'm Marcus. Before spending a cent, let's understand what you're actually paying for.",
        video: { title: "Module 1 Overview", duration: "5:20" },
        lessons: [
          { title: "You pay for attention",
            content: "Ad platforms show your message to people and charge you for views or clicks. Your job is to put a clear, relevant offer in front of the right audience so that attention turns into sales.",
            voiceover: "You're renting attention. The better your offer and targeting, the more that attention turns into buyers." },
          { title: "The offer comes first",
            content: "No ad can save a weak offer or a confusing page. Make sure your product, price, and landing page already convert before you amplify them with money.",
            voiceover: "Ads amplify what exists. Fix the offer first, or you'll just pay to show people something that doesn't sell." },
        ],
        quiz: [
          q("With paid ads, you mainly pay for…", ["free products", "attention (views or clicks)", "nothing", "shipping"], 1),
          q("Before running ads, you should…", ["make sure the offer and page convert", "spend everything", "ignore the page", "hide the price"], 0),
        ],
      },
      {
        title: "Building a Simple Ad",
        avatar: "marcus",
        avatarLine: "A good beginner ad is clear, honest, and built around one idea.",
        video: { title: "Module 2 Overview", duration: "5:00" },
        lessons: [
          { title: "One offer, one message",
            content: "Show one product, state one clear benefit, and give one clear call to action. Clutter confuses; clarity converts. A clean image and honest words beat flashy hype.",
            voiceover: "One thing, said clearly. The confused mind never clicks — so make the next step obvious." },
          { title: "Targeting the right people",
            content: "Point your ad at the audience most likely to want the product. A smaller, relevant audience usually beats a huge, random one for both cost and results.",
            voiceover: "Right message, right people. A tight, relevant audience spends your budget far more wisely." },
        ],
        quiz: [
          q("A good beginner ad has…", ["five offers", "one offer, one message, one call to action", "no message", "only hype"], 1),
          q("Better targeting means…", ["a huge random audience", "a smaller, relevant audience", "no audience", "everyone"], 1),
        ],
      },
      {
        title: "Testing on a Small Budget",
        avatar: "marcus",
        avatarLine: "Never bet the farm on an untested ad. Let's test the smart, cheap way.",
        video: { title: "Module 3 Overview", duration: "5:10" },
        lessons: [
          { title: "Start small, one variable",
            content: "Begin with a small daily budget and change only one thing at a time — the image, the audience, or the message. That way you learn exactly what moved the results.",
            voiceover: "Small budgets, single changes. That's how you learn cause and effect instead of just guessing." },
          { title: "Give it a fair chance",
            content: "Let a test run long enough to gather real data before judging it. Reacting to a single hour or day leads to bad calls. Patience plus small spend equals clear lessons.",
            voiceover: "Don't panic at hour one. Give the test room to speak, then listen to what the numbers say." },
        ],
        quiz: [
          q("The smart way to test ads is…", ["max budget, many changes", "small budget, one change at a time", "no budget", "no changes ever"], 1),
          q("Before judging a test you should…", ["react in one hour", "let it gather real data", "delete it instantly", "double it blindly"], 1),
        ],
      },
      {
        title: "Reading the Numbers",
        avatar: "marcus",
        avatarLine: "Ads are math with a creative coat. Let's keep the math honest.",
        video: { title: "Module 4 Overview", duration: "5:05" },
        lessons: [
          { title: "Cost vs profit per sale",
            content: "Compare what it costs to get a sale against the profit that sale earns after all fees. If an ad earns more than it costs, it's a winner worth scaling; if not, pause it.",
            voiceover: "One question rules them all: does this ad earn more than it costs after fees? If yes, feed it. If no, stop it." },
          { title: "Scaling winners, cutting losers",
            content: "Slowly increase budget on ads that profit and turn off the ones that don't. Let data — not hope or ego — decide where your money goes.",
            voiceover: "Water the flowers, pull the weeds. Scale what profits, cut what doesn't, and keep your emotions out of it." },
        ],
        quiz: [
          q("An ad is a winner when it…", ["loses money", "earns more than it costs after fees", "gets likes only", "is expensive"], 1),
          q("You should decide budget based on…", ["hope and ego", "data and profitability", "randomness", "the weather"], 1),
        ],
      },
    ],
    finalAssessment: [
      q("With paid ads you pay for…", ["free stock", "attention", "nothing", "refunds"], 1),
      q("Before advertising, ensure…", ["the offer and page convert", "you spend everything", "the page is hidden", "no targeting"], 0),
      q("A good beginner ad has…", ["five offers", "one offer and one clear call to action", "no message", "pure hype"], 1),
      q("Smart testing uses…", ["max budget, many changes", "small budget, one change at a time", "no data", "guessing"], 1),
      q("An ad is worth scaling when…", ["it loses money", "it earns more than it costs after fees", "it gets likes only", "randomly"], 1),
    ],
  },

  {
    slug: "social-commerce",
    title: "TikTok & Social Selling",
    category: "Marketing & Growth",
    level: "Beginner",
    duration: "2h 30m",
    languages: ["English", "Français", "Kreyòl", "Español"],
    rich: true,
    instructor: "priya",
    summary: "Sell through short video and social content — hook attention, tell product stories, and turn followers into buyers authentically.",
    description: "Social platforms like TikTok have become powerful storefronts. This course shows you how short-form video and social content drive sales: how to hook attention in seconds, show products honestly, build a following, and guide viewers to buy — without gimmicks or fake hype.",
    objectives: [
      "Understand how social platforms drive discovery and sales",
      "Create short videos that hook attention and show value",
      "Build an engaged following with consistent, honest content",
      "Guide viewers from interest to purchase authentically",
    ],
    i18n: {
      fr: { title: "TikTok & Vente Sociale", summary: "Vendez grâce à la vidéo courte et au contenu social — captez l'attention, racontez vos produits et convertissez vos abonnés.",
        objectives: ["Comprendre comment les réseaux génèrent découverte et ventes", "Créer des vidéos courtes qui captent l'attention", "Bâtir une communauté engagée avec du contenu honnête", "Guider les spectateurs vers l'achat authentiquement"] },
      ht: { title: "TikTok & Vant sou Rezo", summary: "Vann ak videyo kout ak kontni sosyal — kenbe atansyon, rakonte pwodwi ou, epi transfòme abòne an achtè.",
        objectives: ["Konprann kijan rezo yo bay dekouvèt ak vant", "Kreye videyo kout ki kenbe atansyon", "Bati yon kominote angaje ak kontni onèt", "Gide moun soti nan enterè rive nan achte"] },
      es: { title: "TikTok y Venta Social", summary: "Vende con video corto y contenido social — capta la atención, cuenta historias de producto y convierte seguidores en compradores.",
        objectives: ["Entender cómo las redes generan descubrimiento y ventas", "Crear videos cortos que capten la atención", "Construir una comunidad comprometida con contenido honesto", "Guiar a los espectadores hacia la compra de forma auténtica"] },
    },
    resources: [
      { title: "Short-video hook templates", type: "Doc" },
      { title: "7-day content starter plan", type: "PDF" },
      { title: "Content-to-sale funnel map", type: "Sheet" },
    ],
    modules: [
      {
        title: "How Social Selling Works",
        avatar: "priya",
        avatarLine: "I'm Priya. Social platforms reward content people enjoy — let's make selling feel natural, not pushy.",
        video: { title: "Module 1 Overview", duration: "5:20" },
        lessons: [
          { title: "Discovery over interruption",
            content: "On social platforms, people discover products through content they actually enjoy. Instead of interrupting, you attract — by being useful, entertaining, or relatable while showing your product in action.",
            voiceover: "You're not interrupting a show; you are the show. Earn the watch, and the sale follows naturally." },
          { title: "Content that sells softly",
            content: "The best social selling shows a product solving a real problem or delighting someone. Let viewers imagine themselves using it, then make the next step easy.",
            voiceover: "Show, don't shout. When people can picture themselves with it, they're already half-sold." },
        ],
        quiz: [
          q("Social platforms reward…", ["pushy interruptions", "content people enjoy", "no content", "hidden products"], 1),
          q("The best social selling…", ["shouts and hypes", "shows the product solving a real problem", "hides the product", "ignores viewers"], 1),
        ],
      },
      {
        title: "The Hook & The Story",
        avatar: "priya",
        avatarLine: "You have about three seconds. Let's make them count.",
        video: { title: "Module 2 Overview", duration: "5:10" },
        lessons: [
          { title: "Winning the first seconds",
            content: "Open with a strong hook — a bold statement, a relatable problem, or an eye-catching result. If the first few seconds don't grab attention, the rest is never seen.",
            voiceover: "The scroll is ruthless. A great hook is the price of admission to everything else you want to say." },
          { title: "Telling a quick product story",
            content: "After the hook, show the product doing its job and the benefit it brings. Keep it short, honest, and clear, then end with an obvious next step.",
            voiceover: "Hook, show the win, point the way. Short and honest beats long and salesy every time." },
        ],
        quiz: [
          q("The first seconds of a video should…", ["be boring", "hook attention", "hide the topic", "show nothing"], 1),
          q("After the hook you should…", ["ramble", "show the product's benefit and a next step", "end abruptly", "hide the product"], 1),
        ],
      },
      {
        title: "Building a Following",
        avatar: "priya",
        avatarLine: "Followers come from consistency and connection, not luck.",
        video: { title: "Module 3 Overview", duration: "5:00" },
        lessons: [
          { title: "Consistency and connection",
            content: "Post regularly, reply to comments, and show the human behind the store. A community that feels connected to you buys more and shares your content freely.",
            voiceover: "People follow people. Show up consistently, talk with your audience, and connection turns into loyalty." },
          { title: "Reusing what works",
            content: "When a video does well, make more like it. Study which hooks and topics resonate, and repurpose your best content across formats to reach more people.",
            voiceover: "Do more of what works. Your winners are a map — follow it instead of reinventing every day." },
        ],
        quiz: [
          q("Followers grow mostly from…", ["luck", "consistency and connection", "spam", "silence"], 1),
          q("When a video performs well you should…", ["never repeat it", "make more like it", "delete it", "ignore the data"], 1),
        ],
      },
      {
        title: "From Followers to Buyers",
        avatar: "priya",
        avatarLine: "Attention is nice, but sales pay the bills. Let's connect the two.",
        video: { title: "Module 4 Overview", duration: "5:05" },
        lessons: [
          { title: "Make buying easy",
            content: "Point viewers clearly to where they can buy, keep the path short, and make sure your product page delivers on the video's promise. Friction between interest and checkout kills sales.",
            voiceover: "Interest is fragile. The fewer steps between 'I want it' and 'I bought it,' the more sales you keep." },
          { title: "Honesty keeps trust",
            content: "Only promise what the product truly delivers. Overhyping earns quick clicks but returns and bad reviews. Honest social selling builds a following that buys again and again.",
            voiceover: "Hype once and lose them; be honest and keep them. Trust is the real algorithm that matters." },
        ],
        quiz: [
          q("To turn interest into sales you should…", ["add friction", "make buying easy and keep promises", "hide the link", "overhype"], 1),
          q("Honesty in social selling leads to…", ["returns and bad reviews", "repeat buyers and trust", "no followers", "instant failure"], 1),
        ],
      },
    ],
    finalAssessment: [
      q("Social platforms reward…", ["pushy interruptions", "content people enjoy", "no content", "hidden products"], 1),
      q("A video's first seconds should…", ["be boring", "hook attention", "hide the topic", "show nothing"], 1),
      q("Followers grow from…", ["luck", "consistency and connection", "spam", "silence"], 1),
      q("To convert interest to sales, you…", ["add friction", "make buying easy and keep promises", "hide the link", "overhype"], 1),
      q("Honest social selling builds…", ["returns", "repeat buyers and trust", "no audience", "failure"], 1),
    ],
  },

  {
    slug: "ai-tools-commerce",
    title: "AI Tools for Online Business",
    category: "Technology & AI",
    level: "Beginner",
    duration: "2h 25m",
    languages: ["English", "Français", "Kreyòl", "Español"],
    rich: true,
    instructor: "david",
    summary: "Use AI to work faster and smarter — write listings, create content, analyze data, and serve customers, while keeping a human touch.",
    description: "AI can save a small business hours every week when used well. This course shows practical, honest ways to use AI tools for product listings, marketing content, customer support, and simple analysis — plus the guardrails to keep your brand authentic and your information accurate.",
    objectives: [
      "Understand what AI tools can and cannot do for your business",
      "Use AI to draft listings, content, and customer replies faster",
      "Apply AI to simple analysis and decision support",
      "Keep AI output accurate, honest, and on-brand",
    ],
    i18n: {
      fr: { title: "Outils IA pour le Business en Ligne", summary: "Utilisez l'IA pour travailler plus vite et mieux — fiches produits, contenu, analyse et service — tout en gardant une touche humaine.",
        objectives: ["Comprendre ce que l'IA peut et ne peut pas faire", "Rédiger fiches, contenu et réponses plus vite avec l'IA", "Appliquer l'IA à l'analyse simple", "Garder un résultat exact, honnête et fidèle à la marque"] },
      ht: { title: "Zouti IA pou Biznis Anliy", summary: "Sèvi ak IA pou travay pi vit epi pi entelijan — fè lis, kreye kontni, analize done, sèvi kliyan — san pèdi touch imen an.",
        objectives: ["Konprann sa IA ka fè ak sa li pa ka fè", "Ekri lis, kontni, ak repons pi vit ak IA", "Aplike IA nan analiz senp", "Kenbe rezilta egzat, onèt, epi fidèl ak mak la"] },
      es: { title: "Herramientas de IA para Negocios en Línea", summary: "Usa la IA para trabajar más rápido y mejor — fichas, contenido, análisis y servicio — manteniendo el toque humano.",
        objectives: ["Entender qué puede y qué no puede hacer la IA", "Redactar fichas, contenido y respuestas más rápido con IA", "Aplicar la IA a análisis simples", "Mantener resultados precisos, honestos y de marca"] },
    },
    resources: [
      { title: "AI prompt starter pack", type: "Doc" },
      { title: "Listing & content prompt templates", type: "PDF" },
      { title: "AI accuracy & brand checklist", type: "Sheet" },
    ],
    modules: [
      {
        title: "What AI Can (and Can't) Do",
        avatar: "david",
        avatarLine: "I'm David. AI is a powerful assistant, not a magic button. Let's use it wisely.",
        video: { title: "Module 1 Overview", duration: "5:10" },
        lessons: [
          { title: "A helpful assistant",
            content: "AI is great at drafting text, summarizing, brainstorming, and speeding up repetitive work. It gives you a strong first draft in seconds, freeing your time for judgment and strategy.",
            voiceover: "Think of AI as a fast, tireless intern: brilliant at first drafts, but it still needs your direction and review." },
          { title: "Knowing its limits",
            content: "AI can be confidently wrong, invent details, and miss your brand's nuance. Always review, fact-check anything important, and add the human touch before publishing.",
            voiceover: "AI can sound sure and still be wrong. You are the editor-in-chief — check the facts and keep it honest." },
        ],
        quiz: [
          q("AI is best thought of as…", ["a magic button", "a fast assistant that needs your review", "always correct", "useless"], 1),
          q("A real limit of AI is that it can…", ["never make mistakes", "be confidently wrong", "read minds", "ship products"], 1),
        ],
      },
      {
        title: "AI for Listings & Content",
        avatar: "david",
        avatarLine: "Let's put AI to work on the writing that eats your time.",
        video: { title: "Module 2 Overview", duration: "5:20" },
        lessons: [
          { title: "Better product listings, faster",
            content: "Give AI the product's key facts and audience, and ask for a clear, benefit-focused description. Then edit for accuracy and your brand voice. You get quality drafts in a fraction of the time.",
            voiceover: "Feed it the facts, ask for benefits, then polish. AI drafts; you make it true and on-brand." },
          { title: "Content ideas and drafts",
            content: "Use AI to brainstorm post ideas, outline emails, and draft captions. Keep your unique voice and real experience — AI accelerates the work but shouldn't replace your authenticity.",
            voiceover: "Let AI break the blank page, then bring your real voice. Speed from the tool, soul from you." },
        ],
        quiz: [
          q("When using AI for a listing you should…", ["publish it unread", "give it facts, then edit for accuracy and voice", "hide the product", "skip editing"], 1),
          q("AI content should keep your…", ["nothing", "authentic voice and real experience", "competitor's name", "errors"], 1),
        ],
      },
      {
        title: "AI for Service & Analysis",
        avatar: "david",
        avatarLine: "AI can speed up support and help you make sense of your numbers.",
        video: { title: "Module 3 Overview", duration: "5:00" },
        lessons: [
          { title: "Faster, kinder support drafts",
            content: "AI can draft polite, clear replies to common questions, which you review and personalize. This speeds up support while keeping the empathy customers value.",
            voiceover: "Draft with AI, deliver with heart. Faster replies are only better if they still feel human." },
          { title: "Simple analysis support",
            content: "AI can help summarize reviews, spot themes in feedback, and explain basic numbers in plain language — useful inputs for decisions, as long as you verify the underlying data.",
            voiceover: "AI is a great thinking partner for patterns and summaries — just double-check the data behind its conclusions." },
        ],
        quiz: [
          q("AI support replies should be…", ["sent unread", "reviewed and personalized", "rude", "ignored"], 1),
          q("For analysis, AI is useful for…", ["inventing data", "summarizing and spotting themes (with verification)", "replacing all judgment", "nothing"], 1),
        ],
      },
      {
        title: "Staying Accurate & On-Brand",
        avatar: "david",
        avatarLine: "The magic is real only when the output is true. Let's set your guardrails.",
        video: { title: "Module 4 Overview", duration: "5:05" },
        lessons: [
          { title: "Fact-check and edit",
            content: "Never publish important AI output without checking facts, prices, and claims. A quick review protects you from errors that could mislead customers or damage trust.",
            voiceover: "Trust, but verify — especially prices and promises. One unchecked claim can cost you a customer's trust." },
          { title: "Keeping your brand human",
            content: "Blend AI speed with your genuine voice, values, and care. Customers connect with people; use AI to serve them faster, not to sound like a robot.",
            voiceover: "Use AI to gain time, then spend that time being more human with your customers. That balance is the real edge." },
        ],
        quiz: [
          q("Before publishing important AI output you should…", ["nothing", "fact-check and edit it", "hide it", "trust it blindly"], 1),
          q("The best use of AI's time savings is to…", ["sound robotic", "be more human with customers", "ignore customers", "cut quality"], 1),
        ],
      },
    ],
    finalAssessment: [
      q("AI is best used as…", ["a magic button", "a fast assistant you review", "always right", "useless"], 1),
      q("A real AI limit is that it can…", ["never err", "be confidently wrong", "read minds", "ship boxes"], 1),
      q("AI-written listings should be…", ["published unread", "edited for accuracy and voice", "hidden", "left with errors"], 1),
      q("AI support replies should be…", ["sent unread", "reviewed and personalized", "rude", "ignored"], 1),
      q("Before publishing key AI output you must…", ["nothing", "fact-check and edit", "hide it", "trust blindly"], 1),
    ],
  },

  {
    slug: "order-management",
    title: "Customer Service & Order Management",
    category: "Operations & Compliance",
    level: "Beginner",
    duration: "2h 20m",
    languages: ["English", "Français", "Kreyòl", "Español"],
    rich: true,
    instructor: "priya",
    summary: "Run orders smoothly from checkout to delivery — track status, communicate proactively, handle issues, and keep customers loyal.",
    description: "Great products still need great fulfillment. This course connects customer service with order management: how to process orders accurately, keep customers informed at each step, handle delays, returns, and refunds fairly, and turn smooth operations into repeat business and strong reviews.",
    objectives: [
      "Process orders accurately from checkout to delivery",
      "Keep customers informed proactively at each stage",
      "Handle delays, returns, and refunds fairly and calmly",
      "Use smooth operations to build loyalty and better reviews",
    ],
    i18n: {
      fr: { title: "Service Client & Gestion des Commandes", summary: "Gérez les commandes du paiement à la livraison — suivi, communication proactive, gestion des problèmes et fidélisation.",
        objectives: ["Traiter les commandes avec précision", "Informer les clients à chaque étape", "Gérer retards, retours et remboursements équitablement", "Transformer des opérations fluides en fidélité"] },
      ht: { title: "Sèvis Kliyan & Jesyon Kòmand", summary: "Jere kòmand soti nan peman rive nan livrezon — swiv estati, kominike davans, jere pwoblèm, epi kenbe kliyan fidèl.",
        objectives: ["Trete kòmand ak presizyon", "Enfòme kliyan nan chak etap", "Jere reta, retou, ak ranbousman ak jistis", "Sèvi ak operasyon fasil pou bati fidelite"] },
      es: { title: "Servicio al Cliente y Gestión de Pedidos", summary: "Gestiona pedidos del pago a la entrega — seguimiento, comunicación proactiva, manejo de problemas y fidelización.",
        objectives: ["Procesar pedidos con precisión", "Informar a los clientes en cada etapa", "Manejar retrasos, devoluciones y reembolsos con justicia", "Convertir operaciones fluidas en lealtad"] },
    },
    resources: [
      { title: "Order-processing checklist", type: "PDF" },
      { title: "Status-update message templates", type: "Doc" },
      { title: "Returns & refunds decision guide", type: "Sheet" },
    ],
    modules: [
      {
        title: "From Checkout to Fulfillment",
        avatar: "priya",
        avatarLine: "I'm Priya. A smooth order is invisible to the customer — that's the goal. Let's build the flow.",
        video: { title: "Module 1 Overview", duration: "5:00" },
        lessons: [
          { title: "Processing orders accurately",
            content: "Confirm the order details, forward them correctly to your supplier or fulfillment, and double-check the shipping address. Small accuracy habits prevent most delivery problems before they start.",
            voiceover: "Accuracy up front saves headaches later. Check the address twice; fix mistakes before the box moves." },
          { title: "The order lifecycle",
            content: "Every order moves through stages: placed, processing, shipped, delivered. Knowing the lifecycle lets you tell customers exactly where their order is and what happens next.",
            voiceover: "Know the journey so you can narrate it. Customers relax when they know exactly where their order stands." },
        ],
        quiz: [
          q("A key accuracy habit is to…", ["skip the address", "double-check the shipping address", "guess details", "ignore the order"], 1),
          q("The order lifecycle stages include…", ["placed, processing, shipped, delivered", "only 'sold'", "no stages", "random steps"], 0),
        ],
      },
      {
        title: "Proactive Communication",
        avatar: "priya",
        avatarLine: "The secret to fewer complaints? Tell customers what's happening before they ask.",
        video: { title: "Module 2 Overview", duration: "5:10" },
        lessons: [
          { title: "Updates at each step",
            content: "Send a confirmation, a shipping notice, and a delivery follow-up. Proactive updates reassure customers, reduce 'where is my order?' messages, and build trust automatically.",
            voiceover: "A quick update at each step does the work of ten support replies. Inform early, and anxiety never starts." },
          { title: "Setting honest expectations",
            content: "State realistic delivery windows upfront and confirm them in messages. Customers accept honest timelines; it's the silent surprises that create anger and refunds.",
            voiceover: "Promise honestly, then keep it. Told-in-advance is patient; surprised is upset." },
        ],
        quiz: [
          q("Proactive updates help by…", ["increasing complaints", "reassuring customers and reducing 'where is my order' messages", "hiding status", "slowing delivery"], 1),
          q("Delivery windows should be…", ["hidden", "honest and stated upfront", "always understated", "never mentioned"], 1),
        ],
      },
      {
        title: "Handling Problems",
        avatar: "priya",
        avatarLine: "Something will eventually go wrong. How you handle it defines your brand.",
        video: { title: "Module 3 Overview", duration: "5:05" },
        lessons: [
          { title: "Delays and lost packages",
            content: "When an order is delayed or lost, respond quickly, apologize sincerely, and offer a clear fix — an update, a replacement, or a refund. Speed and ownership turn a bad moment into loyalty.",
            voiceover: "Own it fast. A quick, caring fix to a lost package can create a more loyal customer than a perfect delivery." },
          { title: "Fair returns and refunds",
            content: "Use clear, consistent return and refund rules so every customer is treated fairly. Consistency prevents disputes and keeps your reputation strong.",
            voiceover: "Fair and consistent beats generous-then-stingy. Clear rules protect both your customers and your business." },
        ],
        quiz: [
          q("Best response to a lost package?", ["ignore it", "respond fast, apologize, offer a clear fix", "blame the customer", "cancel silently"], 1),
          q("Returns and refunds should be…", ["random", "clear and consistent", "always refused", "hidden"], 1),
        ],
      },
      {
        title: "Turning Service Into Loyalty",
        avatar: "priya",
        avatarLine: "Smooth operations aren't just tidy — they're your best growth engine.",
        video: { title: "Module 4 Overview", duration: "4:50" },
        lessons: [
          { title: "The after-sale touch",
            content: "A short thank-you, a delivery check-in, and an easy way to get help make customers feel cared for. These small touches earn reviews, referrals, and repeat orders.",
            voiceover: "The sale isn't the end; it's the start of loyalty. A warm after-sale touch brings customers back." },
          { title: "Reviews and repeat business",
            content: "Invite happy customers to leave reviews and let them know new products are coming. Great service plus a gentle invitation turns one-time buyers into regulars.",
            voiceover: "Serve well, then simply ask. Happy customers are glad to review and return when you make it easy." },
        ],
        quiz: [
          q("An after-sale thank-you and check-in helps…", ["annoy customers", "earn reviews, referrals, and repeat orders", "hide the brand", "nothing"], 1),
          q("To grow repeat business you should…", ["never follow up", "serve well and gently invite reviews", "ignore customers", "hide new products"], 1),
        ],
      },
    ],
    finalAssessment: [
      q("A key order-accuracy habit is…", ["skipping the address", "double-checking the shipping address", "guessing", "ignoring orders"], 1),
      q("Proactive updates mainly…", ["increase complaints", "reassure customers and cut 'where is my order' messages", "hide status", "slow delivery"], 1),
      q("Delivery windows should be…", ["hidden", "honest and stated upfront", "understated", "never mentioned"], 1),
      q("Best response to a lost package is…", ["ignore it", "respond fast, apologize, offer a fix", "blame the buyer", "cancel silently"], 1),
      q("Returns and refunds should be…", ["random", "clear and consistent", "always refused", "hidden"], 1),
    ],
  },
];
